import { useState } from "react";
import { useSessionContext } from "ui/context/SessionContext";
import { useNotifications } from "ui/context/NotificationsContext";
import UploadManager from "core/services/uploadManager";
import { updateMetaVariantsFromFile } from "core/services/site/fragment";
import { Fragment } from "core/models";

interface HookReturn {
    upload(
        fragment: Fragment,
        language: string,
        subtitleFileUrl: string,
    ): Promise<
        | {
              updatedFragment: Fragment;
              notificationId: string;
          }
        | undefined
    >;
    uploadPending: boolean;
    uploadError?: string;
}

const useSaveCaptions = (): HookReturn => {
    const { addNotification, updateNotification } = useNotifications();
    const sessionContext = useSessionContext();

    const [uploadPending, setUploadPending] = useState<boolean>(false);
    const [uploadError, setUploadError] = useState<string | undefined>();

    const upload = async (
        fragment: Fragment,
        language: string,
        subtitleFileUrl: string,
    ): Promise<
        | {
              updatedFragment: Fragment;
              notificationId: string;
          }
        | undefined
    > => {
        setUploadPending(true);
        const subtitleBlob = await fetch(subtitleFileUrl).then((x) => x.blob());
        const file = new File([subtitleBlob], "subtitles.vtt");
        const uploadNotification = addNotification(
            "normal",
            "Starting upload",
            "this can take a minute",
            0,
        );
        try {
            if (!sessionContext.session) {
                throw Error("no session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("no selected project");
            }
            const uploadManager = new UploadManager(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
            );
            const uploadedVariant = await uploadManager.uploadVariant(
                file,
                (progress) => {
                    updateNotification(
                        uploadNotification.id,
                        "normal",
                        `Uploading captions (${Math.ceil(progress * 100)}%)`,
                        "This can take a minute",
                        0,
                    );
                },
            );
            if (uploadedVariant.error || !uploadedVariant.value) {
                throw Error("problem uploading captions");
            }
            const updatedFragment = await updateMetaVariantsFromFile(
                sessionContext.session.token,
                fragment.id,
                uploadedVariant.value.id,
                language,
            );
            if (updatedFragment.error || !updatedFragment.value) {
                throw Error("problem while creating captoins");
            }
            updateNotification(
                uploadNotification.id,
                "normal",
                `Captions uploaded`,
                "Now processing clip",
                0,
            );
            setUploadPending(false);
            return {
                updatedFragment: updatedFragment.value,
                notificationId: uploadNotification.id,
            };
        } catch (err) {
            setUploadError((err as Error).message);
            setUploadPending(false);
            return undefined;
        }
    };
    return {
        upload,
        uploadPending,
        uploadError,
    };
};
export default useSaveCaptions;
